import { useTranslation } from '../../../i18n/useTranslation';
import { getBuildingSites, changeBuildingSite } from '../../../components/shopping-cart/services/shopping-cart.service';
import { useEffect, useState } from 'react';
import { IBuildingSiteDto } from '../../../components/order/interfaces/building-site.interface';
import { IBuildingSiteListViewDto } from '../../../components/order/interfaces/building-site-list-view.interface';
import { htmlId } from './building-sites-selector.service';
import CloseIcon from '@mui/icons-material/Close';
import { Utils } from '../../../shared/constants/utilities.constants';
import { useBetween } from '../../../shared/use-between/use-between';
import { ShoppingCartUseBetween } from '../../../components/order/use-between/shopping-cart-use-between';

import {
    Dialog,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    IconButton,
    TextField,
    InputAdornment,
    Stack,
    Pagination,
} from '@mui/material';
import ButtonGroupList, { ButtonItem } from '../../../shared/button-group-list/button-group-list';
import BuildingSitesListSkeleton from '../../building-sites-list/building-sites-list-skeleton';
import { UserDetailsUseBetween } from '../../../shared/use-between/user-details-use-between';
import { updateUserRecentSelection } from '../../../shared/services';
import { IShopingCart } from '../../../components/order/interfaces/shopping-cart.interface';
import { BuildingSiteFormViewModel } from './building-sites-form-view.model';
import { getEditedBuildingSite } from './building-sites-form.service';

interface IBuildingSitesSelectorProp {
    isOpen: boolean;
    handleCloseDialog: Function;
}

const BuildingSitesSelector: React.FC<IBuildingSitesSelectorProp> = ({ isOpen, handleCloseDialog }) => {
    const { t } = useTranslation('translation');
    const [page, setPage] = useState<number>(1);
    const [count, setCount] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(0);
    const [searchInput, setSearchInput] = useState<string>('');
    const [bulidingSites, setBulidingSites] = useState<IBuildingSiteListViewDto[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { setBuildingSiteAddress } = useBetween(ShoppingCartUseBetween);
    const { setBuyerContact } = useBetween(ShoppingCartUseBetween);
    const { userSelection, setUserSelection } = useBetween(UserDetailsUseBetween);
    const { setIsReadyToOrder } = useBetween(ShoppingCartUseBetween);
    const { setBuildingSite } = useBetween(ShoppingCartUseBetween);
    const { setShoppingCart } = useBetween(ShoppingCartUseBetween);

    const handleClose = () => {
        handleCloseDialog();
        handleClearSearch();
    };

    const searchActionButtons: ButtonItem[] = [
        { id: `${htmlId}-search-btn`, label: t('SEARCH') },
        { id: `${htmlId}-clear-search-btn`, label: t('CLEAR_SEARCH') },
    ];

    const handleSearchActionButtonClick = (id: string) => {
        switch (id) {
            case `${htmlId}-search-btn`:
                handleSearch();
                break;
            case `${htmlId}-clear-search-btn`:
                setSearchInput('');
                getAndSetBuildingSites(1, true);
                break;
        }
    };

    const handleResponse = (shopingCart: IShopingCart) => {
        setBuildingSiteAddress(shopingCart.buildingSiteAddress);
        setBuyerContact(shopingCart.buyerContact);
        setIsReadyToOrder(shopingCart.isReadyToOrder);
        setBuildingSite(shopingCart.buildingSite);
    };

    // Sets the building site and clears the (now stale) delivery address/contact in a single save - see
    // changeBuildingSite's own comment for why this replaced three separate, racing update calls.
    const handleChangeBuildingSite = (buildingSiteUuid: string) => {
        getEditedBuildingSite(buildingSiteUuid).then((response) => {
            if (response) {
                const model: BuildingSiteFormViewModel = response.data;
                changeBuildingSite({
                    id: buildingSiteUuid,
                    name: model.name,
                    code: model.code,
                    country: model.country,
                } as IBuildingSiteListViewDto).then((response) => {
                    if (response) {
                        handleResponse(response.data);
                        setShoppingCart(Utils.clone(response.data));
                    }
                });
            }
        });
    };

    const getDto = (page: number, isFromClearSearch: boolean = false) => {
        return {
            data: [],
            count: count,
            page: page,
            searchInput: !isFromClearSearch ? searchInput : '',
            pageSize: pageSize,
            fetchPerUserOnly: false,
        } as IBuildingSiteDto;
    };

    const getAndSetBuildingSites = (page: number, isFromClearSearch: boolean = false) => {
        setIsLoading(true);
        getBuildingSites(getDto(page, isFromClearSearch)).then((response) => {
            if (response) {
                const dto: IBuildingSiteDto = response.data;
                setBulidingSites(dto.data);
                setPage(dto.page);
                setCount(dto.count);
                setPageSize(dto.pageSize);
                if (isFromClearSearch) {
                    setSearchInput('');
                }
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        });
    };

    const updateSelectedBuildingSiteUuid = (buildingSiteUuid: string, parentContractorId: string) => {
        if (userSelection && userSelection.buildingSiteUuid !== buildingSiteUuid) {
            userSelection.buildingSiteUuid = buildingSiteUuid;
            userSelection.parentContractorId = parentContractorId;
            handleChangeBuildingSite(buildingSiteUuid);
            // Unrelated to the cart itself (a separate "remembered selection" record) - independent of the
            // cart save above, not chained before or after it.
            updateUserRecentSelection(userSelection).then((userSelectionResponse) => {
                if (userSelectionResponse) {
                    setUserSelection(Utils.clone(userSelection));
                }
            });
        }
        handleClose();
    };

    const handleChangePage = (page: number) => {
        getAndSetBuildingSites(page);
    };

    const handleSearch = () => {
        getAndSetBuildingSites(1);
    };

    const handleClearSearch = () => {
        getAndSetBuildingSites(1, true);
    };

    const handleChangeSearchValue = (event: any) => {
        setSearchInput(event.target.value);
    };

    const handleSelectedBuildingSite = (item: IBuildingSiteListViewDto) => {
        updateSelectedBuildingSiteUuid(item.id, item.parentContractorId);
    };

    useEffect(() => {
        getAndSetBuildingSites(page);
    }, []);

    return (
        <div>
            <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="md">
                <DialogContent sx={{ position: 'relative' }}>
                    <Typography variant="h6" gutterBottom>
                        {t('SELECT_BUILDING_SITE')}
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <TextField
                            sx={{}}
                            label={t('SEARCH')}
                            variant="outlined"
                            value={searchInput}
                            onChange={handleChangeSearchValue}
                            size="small"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <ButtonGroupList
                                            buttons={searchActionButtons}
                                            size="small"
                                            onClick={(id: string) => {
                                                handleSearchActionButtonClick(id);
                                            }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                            fullWidth
                        />
                    </Box>
                    {isLoading ? (
                        <BuildingSitesListSkeleton count={3} />
                    ) : (
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}
                                        >
                                            <strong>{t('CODE')}</strong>
                                        </TableCell>
                                        <TableCell
                                            sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}
                                        >
                                            <strong>{t('BUILDING_SITE')}</strong>
                                        </TableCell>
                                        <TableCell
                                            sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}
                                        >
                                            <strong>{t('NAME')}</strong>
                                        </TableCell>
                                        <TableCell
                                            sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}
                                        >
                                            <strong>{t('ADDRESS')}</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {bulidingSites.map((item) => (
                                        <TableRow
                                            onClick={() => {
                                                handleSelectedBuildingSite(item);
                                            }}
                                            key={item.code}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: '#f2f2f2',
                                                    cursor: 'pointer',
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {item.code}
                                            </TableCell>
                                            <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {item.buildingSiteName}
                                            </TableCell>
                                            <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {item.name}
                                            </TableCell>
                                            <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                <Box>
                                                    <Typography variant="body2">{item.street}</Typography>
                                                    <Typography variant="body2">
                                                        {item.postalCode} {item.city}
                                                    </Typography>
                                                    <Typography variant="body2">{t(item.countryName)}</Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                        <Pagination
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    color: 'black',
                                    borderColor: '#821818 !important',
                                },
                                '& .Mui-selected': {
                                    backgroundColor: '#821818 !important',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#821818 !important',
                                    },
                                },
                            }}
                            count={count}
                            boundaryCount={1}
                            page={page}
                            onChange={(_, page) => {
                                handleChangePage(page);
                            }}
                        />
                    </Stack>
                </DialogContent>
            </Dialog>
        </div>
    );
};
export default BuildingSitesSelector;
